import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';

import Paper from '@material-ui/core/Paper';

import AvailabilityTable from 'pages/Terminals/components/AvailabilityTable';
import ShiftPicker from 'pages/Terminals/components/ShiftPicker';
import CircularProgress from '@material-ui/core/CircularProgress';
import { STICKY_IE_CLASS } from 'constants/layout';
import classnames from 'classnames';
import SingleContainerTypePicker from 'components/SingleContainerTypePicker';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import { setShippingLinesFiltersAndReduceTerminals, clearFilters } from 'ducks/instructionFilters';
import { ShippingLine, ContainerType } from 'types/Shipping';
import { makeStyles, Theme } from '@material-ui/core';
import isEqual from 'lodash/isEqual';

interface Location {
  pathname: string;
}

interface Props {
  shippingLines: ShippingLine[];
  containerTypes: ContainerType[];
  isLoading: boolean;
  location?: Location;
  displaySelector?: boolean;
  region?: string;
}

const tableClasses = makeStyles((theme: Theme) => ({
  table: {
    margin: 'auto',
    marginTop: theme.spacing(5),
    textAlign: 'center',
    marginBottom: theme.spacing(5),
  },
  loaderWrapper: {
    textAlign: 'center',
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
  },
  tableOver: {
    top: -55,
    width: '100%',
    marginLeft: theme.spacing(1),
    position: 'absolute',
  },
  paper: {
    marginTop: 55,
    position: 'relative',
    display: 'inline-block',
    maxWidth: '100%',
  },
  containerType: {
    width: 780,
    margin: 'auto',
  },
}));

// Helper functions to compare values
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function usePrevious(value: any) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useCompare(val: any) {
  const prevVal = usePrevious(val);
  return !isEqual(val, prevVal);
}
// End helper functions

export const Table: React.FC<Props> = (props) => {
  const { shippingLines, containerTypes, isLoading, location, displaySelector, region } = props;

  const [filterAppplied, setFilterAppplied] = useState(false);

  const dispatch = useDispatch();

  const classes = tableClasses();

  const terminals = useSelector((state: RootStateOrAny) => state.erlData.ports);
  const mapping = useSelector((state: RootStateOrAny) => state.availabilities.mappingShippingLine);

  const shippingLinesChanged = useCompare(shippingLines);

  useEffect(() => {
    if (
      (!filterAppplied || shippingLinesChanged) && // If the filter has not been applied yet or the shipping lines have changed
      terminals &&
      shippingLines &&
      shippingLines.length &&
      mapping &&
      mapping.length
    ) {
      dispatch(setShippingLinesFiltersAndReduceTerminals(shippingLines));
      setFilterAppplied(true);
    }
  }, [filterAppplied, shippingLines, terminals, mapping, dispatch, shippingLinesChanged]);

  // Clears filters when unmounting
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  useLayoutEffect(() => {
    return () => dispatch(clearFilters());
  }, [dispatch]);

  const shippingLine = shippingLines.length ? shippingLines[0] : null;
  const containerType = containerTypes.length ? containerTypes[0] : null;
  const defaultValue =
    shippingLine && containerType
      ? {
          shippingLine,
          containerType,
        }
      : undefined;

  return (
    <>
      {displaySelector && defaultValue && (
        <SingleContainerTypePicker
          classes={{ root: classes.containerType }}
          defaultValue={defaultValue}
          destination={location?.pathname}
        />
      )}
      {isLoading && (
        <div className={classes.loaderWrapper}>
          <CircularProgress size={70} />
        </div>
      )}
      {containerTypes.length > 0 && shippingLines.length > 0 && (
        <div className={classes.table}>
          <Paper className={classes.paper}>
            <div className={classnames(classes.tableOver, STICKY_IE_CLASS)}>
              <ShiftPicker />
            </div>
            <AvailabilityTable
              shippingLines={shippingLines}
              containerSize={containerTypes}
              region={region}
              stickySpace
              disableClickEmpty
              // noFilter={true}
            />
          </Paper>
        </div>
      )}
    </>
  );
};

export default Table;
